import React, { useState } from 'react';
import { PaymentMethod } from '../types/index';
import { X, ArrowLeftRight, Check } from 'lucide-react';

/**
 * Modal para liquidar dívida (total ou parcial)
 */
export default function ModalLiquidateDebt({ isOpen, onClose, debt, onLiquidate }) {
  const [liquidationType, setLiquidationType] = useState('FULL'); // 'FULL' or 'PARTIAL'
  const [paymentMethod, setPaymentMethod] = useState(PaymentMethod.CARD); // CARD or TRANSFER
  const [liquidatedAmount, setLiquidatedAmount] = useState('');
  const [error, setError] = useState('');

  if (!isOpen || !debt) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (liquidationType === 'PARTIAL') {
      const amount = Number(liquidatedAmount);
      if (!amount || amount <= 0) {
        setError('Por favor, insira um valor válido.');
        return;
      }
      if (amount > debt.debtAmount) {
        setError(`O valor não pode exceder a dívida pendente (${debt.debtAmount.toFixed(2)}€).`);
        return;
      }
      onLiquidate({
        type: 'PARTIAL',
        amount,
        paymentMethod
      });
    } else {
      // Liquidação completa
      onLiquidate({
        type: 'FULL',
        amount: debt.debtAmount,
        paymentMethod
      });
    }
    // Não fechar aqui - deixar handleLiquidate tratar após operação assíncrona
    // O reset do formulário acontecerá no handleClose que é chamado pelo handleLiquidate
  };

  const handleClose = () => {
    setLiquidationType('FULL');
    setPaymentMethod(PaymentMethod.CARD);
    setLiquidatedAmount('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-2xl font-black text-slate-900">Liquidar Dívida</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <X size={24} className="text-slate-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Debt Info */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            <p className="text-sm text-slate-500">Cliente</p>
            <p className="font-bold text-slate-900">{debt.client || 'Consumidor Final'}</p>
            <p className="text-sm text-slate-500">Dívida Pendente</p>
            <p className="text-2xl font-black text-red-600">{debt.debtAmount.toFixed(2)}€</p>
          </div>

          {/* Liquidation Type */}
          <div className="space-y-3">
            <p className="text-sm font-bold text-slate-700">Tipo de Liquidação</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setLiquidationType('FULL')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  liquidationType === 'FULL'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                <Check size={20} className="mx-auto mb-2" />
                <p className="font-bold text-sm">Total</p>
              </button>
              <button
                type="button"
                onClick={() => setLiquidationType('PARTIAL')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  liquidationType === 'PARTIAL'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                <ArrowLeftRight size={20} className="mx-auto mb-2" />
                <p className="font-bold text-sm">Parcial</p>
              </button>
            </div>
          </div>

          {/* Partial Amount Input */}
          {liquidationType === 'PARTIAL' && (
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700">
                Valor a Liquidar (€)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={debt.debtAmount}
                value={liquidatedAmount}
                onChange={(e) => setLiquidatedAmount(e.target.value)}
                placeholder={`Máximo: ${debt.debtAmount.toFixed(2)}€`}
                className="w-full p-4 bg-white border-2 border-slate-200 rounded-xl font-bold outline-none focus:border-blue-500"
                required={liquidationType === 'PARTIAL'}
              />
              {liquidatedAmount && Number(liquidatedAmount) > 0 && (
                <p className="text-xs text-slate-500">
                  Restará pendente: {(debt.debtAmount - Number(liquidatedAmount)).toFixed(2)}€
                </p>
              )}
            </div>
          )}

          {/* Payment Method */}
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">Método de Pagamento</label>
            <select
              value={paymentMethod}
              onChange={(e) => {
                const value = e.target.value;
                // Permitir DINHEIRO, MULTIBANCO ou TRANSFERÊNCIA
                if (value === PaymentMethod.CASH || value === PaymentMethod.CARD || value === PaymentMethod.TRANSFER || value === PaymentMethod.CHEQUE) {
                  setPaymentMethod(value);
                }
              }}
              className="w-full p-4 bg-white border-2 border-slate-200 rounded-xl font-black outline-none focus:border-blue-500 appearance-none"
            >
              <option value={PaymentMethod.CASH}>Dinheiro</option>
              <option value={PaymentMethod.CARD}>Multibanco</option>
              <option value={PaymentMethod.TRANSFER}>Transferência</option>
              <option value={PaymentMethod.CHEQUE}>Cheque</option>
            </select>
            <p className="text-xs text-slate-400">
              Selecione Dinheiro, Multibanco ou Transferência para liquidar
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-600 font-bold">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 p-4 bg-slate-100 hover:bg-slate-200 rounded-xl font-bold text-slate-700 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 p-4 bg-emerald-600 hover:bg-emerald-700 rounded-xl font-bold text-white transition-colors flex items-center justify-center gap-2"
            >
              <Check size={20} />
              Liquidar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

